import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, IconButton, Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faClipboard, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

const VERSE_PATTERN = /["\u201c\u201d]([^"\u201c\u201d]{15,200})["\u201c\u201d]\s*[-\u2013\u2014]\s*((?:Jo\u00e3o|Salmos?|Sl|Mt|Mateus|Mc|Marcos|Lc|Lucas|At|Atos|Rm|Romanos|1Co|2Co|Gl|Ef|Fp|Cl|1Ts|2Ts|1Tm|2Tm|Tt|Hb|Tg|1Pe|2Pe|1Jo|Ap|Gn|Ex|Lv|Nm|Dt|Js|Jz|Rt|1Sm|2Sm|1Rs|2Rs|1Cr|2Cr|Ed|Ne|Est|J\u00f3|Pr|Ec|Is|Jr|Ez|Dn|Os|Jl|Am|Jn|Mq|Zc|Ml)\s+\d+:\d+)/i;

function extractVerse(content) {
  if (!content) return null;
  const match = content.match(VERSE_PATTERN);
  if (!match) return null;
  const text = match[1].length > 140 ? match[1].substring(0, 140) + '...' : match[1];
  return { text, ref: match[2] };
}

function generateWhatsAppMessage(post) {
  const verse = extractVerse(post.content || '');
  const url = `https://feescrita.com/posts/${post.id}`;

  let message = `\u271d\ufe0f *${post.title}*\n\n`;

  if (verse) {
    message += `_\u201c${verse.text}\u201d_\n\u2014 ${verse.ref}\n\n`;
  }

  message += `\ud83d\ude4f Leia e seja aben\u00e7oado!\n\n\ud83d\udd17 ${url}`;
  return message;
}

const WhatsAppShare = ({ post }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const message = generateWhatsAppMessage(post);
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Tooltip title="Compartilhe essa b\u00en\u00e7\u00e3o com quem voc\u00ea ama">
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
          sx={{
            backgroundColor: '#356D52',
            color: 'white',
            fontFamily: "'Albert Sans', sans-serif",
            fontWeight: 600,
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: '999px',
            boxShadow: 'none',
            px: { xs: 2, sm: '20px' },
            py: '9px',
            '&:hover': { backgroundColor: '#365E4B', boxShadow: 'none' },
            '&:active': { transform: 'scale(0.98)' },
          }}
          aria-label="Compartilhar post no WhatsApp"
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Compartilhar no WhatsApp
          </Box>
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          📱 Compartilhar no WhatsApp
          <IconButton onClick={() => setOpen(false)} size="small" aria-label="Fechar">
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              backgroundColor: '#DCF8C6',
              borderRadius: '10px',
              p: 2,
              whiteSpace: 'pre-wrap',
              fontSize: '14px',
              lineHeight: 1.7,
              fontFamily: '"Segoe UI", sans-serif',
            }}
            aria-label="Pr\u00e9via da mensagem do WhatsApp"
          >
            {message}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2.5 }}>
          <Button
            variant="outlined"
            onClick={handleCopy}
            startIcon={<FontAwesomeIcon icon={copied ? faCheck : faClipboard} />}
            sx={{
              color: copied ? 'green' : undefined,
              borderColor: copied ? 'green' : undefined,
            }}
            aria-label="Copiar texto da mensagem"
          >
            {copied ? 'Copiado!' : 'Copiar Texto'}
          </Button>

          <Button
            variant="contained"
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
            sx={{
              backgroundColor: '#25D366',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#128C7E' },
            }}
            aria-label="Enviar no WhatsApp"
          >
            Enviar no WhatsApp →
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhatsAppShare;
